import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AddInstance, ClearInstance } from 'app/area/favourite/action-types';
import {
  selectDefinition,
  selectFetchingDefinition,
  selectFetchingUpdateReplaceDomainNodes,
  selectFetchingReportCategory,
  selectFetchingSortOptions,
  selectPresentationTypeForSelectedReport,
  selectReportCategory,
  selectSearchRequiredUsers,
  selectSortOptions,
  selectSortOrderForSelectedReport,
  selectUrlMultipleSuppliers,
  selectValidateFavouriteFilters,
  selectWorkingDefinition,
  selectFetchingExtendedReplaceDomainNodes
} from 'app/area/favourite/selectors';
import { actionCreators } from './actions';
import { bindActionCreators } from 'redux';
import { DefaultSortOrder } from 'ui/sort-order/types';
import { returnSearchQuery } from 'app/area/favourite/search-query';
import { useInstanceKey } from 'app/area/favourite/favourite-context';
import { useCanSaveReport } from 'app/area/company-report/hooks';
import { useTranslation } from 'app/infrastructure/translate/hooks';
import { STATE } from 'app/constants';
import { searchTranslation } from 'app/area/favourite/constants';
import { AppDispatch } from 'app/infrastructure/configure-store';
import { mapSortOrder } from 'app/area/favourite/mapping';

export function useInstanceSlice() {
  const instanceKey = useInstanceKey();
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    dispatch(new AddInstance(instanceKey));
    return () => {
      dispatch(new ClearInstance(instanceKey));
    };
  }, [instanceKey]);
}

export function useSavedReportState(editReport: boolean, validateBeforeSave: boolean = false) {
  const [createReport, setCreateReport] = useState(editReport);
  const [updateExistingReport, setUpdateExistingReport] = useState(editReport);
  const { validateFavouriteFilters } = useActionCreators();
  const canSaveReport = validateBeforeSave ? useCanSaveReport() : true;
  const translation = useTranslation(STATE.favourite, searchTranslation);

  const onCreateReport = useCallback(() => {
    if(canSaveReport) {
      setUpdateExistingReport(false);
      setCreateReport(true);
    }
    else {
      validateFavouriteFilters(translation);
    }    
  }, [canSaveReport, setUpdateExistingReport, setCreateReport, validateFavouriteFilters]);

  const onCloseReport = useCallback(() => {
    setUpdateExistingReport(false);
    setCreateReport(false);
  }, [setUpdateExistingReport, setCreateReport]);

  const onUpdateReport = useCallback(() => {
    if(canSaveReport) {
      setUpdateExistingReport(true);
      setCreateReport(true);
    }
    else {
      validateFavouriteFilters(translation);
    }
  }, [canSaveReport, setUpdateExistingReport, setCreateReport, validateFavouriteFilters]);
  
  return {
    createReport,
    updateExistingReport,
    onCreateReport,
    onCloseReport,
    onUpdateReport
  };
}

export function useFavouriteDefinitions() {
  const instanceKey = useInstanceKey();
  const fetchingDefinition = useSelector(selectFetchingDefinition(instanceKey));
  const definition = useSelector(selectDefinition(instanceKey));
  const workingDefinition = useSelector(selectWorkingDefinition(instanceKey));

  const {
    transformWorkingDefinition: updateDefinition,
    updateReplaceDomainNodes,
    loadExtendedReplaceDomainNodes
  } = useActionCreators();
  
  return {
    definition,
    workingDefinition,
    updateDefinition,
    updateReplaceDomainNodes,
    loadExtendedReplaceDomainNodes,
    fetchingDefinition
  };
}

export function useReplaceDomainNodes() {
  return {
    fetchingUpdateReplaceDomainNodes: useSelector(selectFetchingUpdateReplaceDomainNodes(useInstanceKey())),
    fetchingExtendedReplaceDomainNodes: useSelector(selectFetchingExtendedReplaceDomainNodes(useInstanceKey()))
  };
}

export function useLoadFavouriteDefinitionEffect(favouriteId: number, embeddedFavourite?: boolean) {
  const instanceKey = useInstanceKey();

  const {
    clearResults,
    loadFavouriteDefinition,
    loadFavouriteDefinitionEmbedded
  } = useActionCreators();

  useEffect(() => {
    clearResults();
    if (embeddedFavourite) {
      favouriteId && loadFavouriteDefinitionEmbedded({
        favouriteId
      });
    }
    else {
      loadFavouriteDefinition({
        favouriteId,
        location: window.location
      });
    }
  }, [
    instanceKey, 
    favouriteId,
    embeddedFavourite
  ]);
}

export function useSearchQuery() {
  const instanceKey = useInstanceKey();

  const {
    setWorkingDefinitionQuery
  } = useActionCreators();

  const definition = useSelector(selectDefinition(instanceKey));

  const [searchQueryTemp, setSearchQueryTemp] = useState('');

  const clearSearch = useCallback(() => {
    setSearchQueryTemp('');
  }, [setSearchQueryTemp]);

  const onChange = useCallback((newSearchQueryTemp: string) => {
    if (!newSearchQueryTemp || newSearchQueryTemp === '') {
      clearSearch();
      setWorkingDefinitionQuery('');
    }
    else {
      setSearchQueryTemp(newSearchQueryTemp);
    }
  }, [setWorkingDefinitionQuery]);
  
  const onSearch = useCallback(() => {
    setWorkingDefinitionQuery(searchQueryTemp);
  }, [setWorkingDefinitionQuery, searchQueryTemp]);

  useEffect(() => {
    const searchQueryFromDefinition = returnSearchQuery(definition?.conditions ?? []) || '';
    setSearchQueryTemp(searchQueryFromDefinition);
  }, [definition]);

  return {
    searchQueryTemp,
    onChange,
    onSearch
  };
}

export function useReport(
  overrideReport?: number,
  loadReportCategories?: boolean
) {
  const instanceKey = useInstanceKey();

  const {
    loadReportCategory,
    setWorkingDefinitionSelectedReport
  } = useActionCreators();

  const fetchingReportCategory = useSelector(selectFetchingReportCategory(instanceKey));
  const reportCategory = useSelector(selectReportCategory(instanceKey)) || [];
  const workingDefinition = useSelector(selectWorkingDefinition(instanceKey));

  const report = workingDefinition?.selectedReport?.v;

  useEffect(() => {
    if (loadReportCategories) {
      loadReportCategory();
    }
  }, [instanceKey, loadReportCategories]);

  useEffect(() => {
    if (workingDefinition && overrideReport !== report ) {
      setWorkingDefinitionSelectedReport(overrideReport);
    }
  }, [setWorkingDefinitionSelectedReport, report, workingDefinition, workingDefinition?.selectedReport?.v, overrideReport]);

  return {
    fetchingReportCategory,
    reportCategory,
    report
  };
}

export function useSortOptions(
  report: number
) {
  const instanceKey = useInstanceKey();
  const fetchingSortOptions = useSelector(selectFetchingSortOptions(instanceKey));
  const sortOptions = useSelector(selectSortOptions(instanceKey));
  const sortOrderFields = mapSortOrder(useSelector(selectSortOrderForSelectedReport(instanceKey)) || []);

  const {
    loadSortOptions,
    setWorkingDefinitionSortOptions
  } = useActionCreators();

  const loadSortOptionsForCurrentReport = useCallback(
    (query?: string) => loadSortOptions(report, query), 
    [report]
  );

  const updateSortOptions = useCallback((fields: DefaultSortOrder[]) => {
    setWorkingDefinitionSortOptions(report, fields);
  }, [report]);

  return {
    fetchingSortOptions,
    loadSortOptions: loadSortOptionsForCurrentReport,
    updateSortOptions,
    sortOptions,
    sortOrderFields
  };
}

export function useActionCreators() {
  const instanceKey = useInstanceKey();
  const dispatch = useDispatch();
  return useMemo(() => bindActionCreators(actionCreators(instanceKey), dispatch), [instanceKey]);
}

export function useSearchRequiredUsers() {
  const instanceKey = useInstanceKey();
  return useSelector(selectSearchRequiredUsers(instanceKey));
}

export function useUrlMultipleSuppliers() {
  const instanceKey = useInstanceKey();
  return useSelector(selectUrlMultipleSuppliers(instanceKey));
}

export function usePresentationType() {
  const instanceKey = useInstanceKey();
  return useSelector(selectPresentationTypeForSelectedReport(instanceKey));
}

export function useValidateFavouriteFilters() {
  const instanceKey = useInstanceKey();
  return useSelector(selectValidateFavouriteFilters(instanceKey));
}
